import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { languagesQuery, useApi } from '../../lib/api';
import { languageStore, setLevers, startLanguage } from './state';
import type { DetectEnv } from './detect';

/** The longest a visit not in English waits for its copy and the levers before showing English. */
export const BOOT_WAIT_MS = 2500;

let started: ReturnType<typeof startLanguage> | null = null;

/** Tests: start detection again. */
export function resetLanguageBoot() {
  started = null;
}

/**
 * The app's first language (detect.ts), before the first screen:
 * - English: shown at once; the levers are read when the sheet opens.
 * - Any other: the screen waits (up to BOOT_WAIT_MS) for the language's chunk, its font faces
 *   and `GET /languages`, so a visitor sees neither English first nor a language whose lever
 *   is off. The API unreachable: every language is on. Too slow: English now, theirs when in.
 */
export function LanguageBoot({ env, children }: { env?: DetectEnv; children: ReactNode }) {
  const api = useApi();
  const qc = useQueryClient();
  const [ready, setReady] = useState(() => {
    started ??= startLanguage(env);
    return languageStore.get().chosen === 'en';
  });

  useEffect(() => {
    if (ready || !started) return;
    const done = () => setReady(true);
    const timer = setTimeout(done, BOOT_WAIT_MS);
    const levers = qc.fetchQuery(languagesQuery(api)).then(
      (list) => setLevers(list),
      () => setLevers(null),
    );
    void Promise.all([started.ready, levers]).finally(() => {
      clearTimeout(timer);
      done();
    });
    return () => clearTimeout(timer);
  }, [ready, api, qc]);

  return ready ? <>{children}</> : null;
}
